import { createContext, useContext, useMemo, useState } from 'react';

import { getPhaseFromHash } from 'App/Helpers';
import { useCustomDetectBot } from 'App/hooks';

import { useGamblingBootstrap } from './hooks/useGamblingBootstrap';
import { useGamblingFormSubmit } from './hooks/useGamblingFormSubmit';
import { useGamblingNextPhase } from './hooks/useGamblingNextPhase';
import { useGamblingReqHandler } from './hooks/useGamblingReqHandler';
import { useGamblingSharpstarService } from './hooks/useGamblingSharpstarService';
import { useSyncedPhaseFromHash } from './hooks/useSyncedPhaseFromHash';

const GamblingAppStateProviderContext = createContext(null);

/**
 * Holds the shared state of a gambling landing (URL params, offers, phase, lead form and
 * reCAPTCHA state) and exposes it to the tree through context.
 *
 * @param {object} props - Component props.
 * @param {object} props.config - Landing build config from `__LP_CONFIG__` (env, lang, source, site and fallback URLs, campaign id, Sharpstar URL, reCAPTCHA keys, OneSignal app id, flags).
 * @param {import("react").ReactNode} props.children - Landing content that reads the shared state.
 * @returns {import("react").JSX.Element} - The context provider wrapping `children`.
 */
const GamblingAppStateProvider = ({ config, children }) => {
    const {
        env,
        lang,
        source,
        siteUrl,
        fallbackUrl,
        campaignId,
        sharpstarApiUrl,
        reCaptchasiteKey2,
        reCaptchasiteKey3,
        oneSignalAppId,
        checkLeadHash,
        sentryEnabled,
    } = config;
    const [urlParams, setUrlParams] = useState({
        rid: undefined,
        refid1: undefined,
        refid2: undefined,
        refid3: undefined,
        hash: undefined,
    });
    const [offers, setOffers] = useState(null);
    const [extraData, setExtraData] = useState({});
    const [phase, setPhase] = useState(() => Number(getPhaseFromHash()) || 1);
    const [leadStatus, setLeadStatus] = useState({});
    const [captchaV2TriggerCode, setCaptchaV2TriggerCode] = useState(null);
    const [tokenV2, setTokenV2] = useState(null);

    const botInfo = useCustomDetectBot();

    useSyncedPhaseFromHash(phase, setPhase);

    const { cookiesService, sharpstarService } = useGamblingSharpstarService({
        sharpstarApiUrl,
        source,
        campaignId,
    });

    const nextPhase = useGamblingNextPhase(setPhase);

    const reqHandler = useGamblingReqHandler({
        siteUrl,
        refid1: urlParams.refid1,
        lang,
        env,
        setUrlParams,
        setOffers,
        setLeadStatus,
        setCaptchaV2TriggerCode,
    });

    useGamblingBootstrap({
        botInfo,
        siteUrl,
        env,
        sentryEnabled,
        source,
        campaignId,
        checkLeadHash,
        sharpstarService,
        cookiesService,
        fallbackUrl,
        setUrlParams,
        setLeadStatus,
        setOffers,
    });

    const { formSubmit, formSubmitState, tokenV3 } = useGamblingFormSubmit({
        captchaV2TriggerCode,
        reCaptchasiteKey3,
        extraData,
        setExtraData,
        botInfo,
        leadStatus,
        offers,
        sharpstarService,
        leadHash: urlParams.hash,
        tokenV2,
        reqHandler,
        nextPhase,
    });

    const values = useMemo(
        () => ({
            ...config,
            nextPhase,
            oneSignalAppId,
            sharpstarService,
            captchaV2TriggerCode,
            urlParams,
            setExtraData,
            setTokenV2,
            tokenV2,
            tokenV3,
            reCaptchasiteKey2,
            formSubmit,
            formSubmitState,
            extraData,
            offers,
            phase,
            leadStatus,
            reqHandler,
        }),
        [
            config,
            nextPhase,
            oneSignalAppId,
            sharpstarService,
            captchaV2TriggerCode,
            urlParams,
            tokenV2,
            tokenV3,
            reCaptchasiteKey2,
            formSubmit,
            formSubmitState,
            extraData,
            offers,
            phase,
            leadStatus,
            reqHandler,
        ]
    );

    return (
        <GamblingAppStateProviderContext value={values}>{children}</GamblingAppStateProviderContext>
    );
};

/**
 * Reads the shared gambling landing state from the nearest `GamblingAppStateProvider`.
 *
 * @returns {object} - Landing config plus offers, phase, URL params, lead form state and the actions that change them.
 * @throws {Error} - When called outside `GamblingAppStateProvider`.
 */
export const useGamblingAppStateProvider = () => {
    const context = useContext(GamblingAppStateProviderContext);

    if (!context) {
        throw new Error('useGamblingAppStateProvider must be used within GamblingAppStateProvider');
    }

    return context;
};

export default GamblingAppStateProvider;
